import { useCallback } from 'react';
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { EasykitTaskCard } from './EasykitTaskCard';
import { EasykitOverlayCard } from './EasykitOverlayCard';
import { EasykitDayStatusBadge } from './EasykitDayStatusBadge';
import { useEasykitPlanningStore } from '@/stores/easykitPlanning';
import { taskDndId, dndCellId } from '@/utils/easykit';
import type { OdooTask } from '@/types/odoo';
import type { EasykitOverlayTask } from '@/api/easykit';

function getDayStatusBg(status: string | null): string {
  switch (status) {
    case 'LEV': return 'bg-amber-50';
    case '.': return 'bg-red-50';
    default: return 'bg-green-50'; // none, null, OPEN = available
  }
}

interface EasykitDayCellProps {
  dateKey: string;
  driverId: number;
  tasks: OdooTask[];
  overlayTasks?: EasykitOverlayTask[];
  isToday: boolean;
  selectedTaskId?: number | null;
}

export function EasykitDayCell({ dateKey, driverId, tasks, overlayTasks = [], isToday, selectedTaskId }: EasykitDayCellProps) {
  const dayMarkers = useEasykitPlanningStore((s) => s.dayMarkers);
  const setDayStatus = useEasykitPlanningStore((s) => s.setDayStatus);
  const moveMode = useEasykitPlanningStore((s) => s.moveMode);
  const executeMoveTask = useEasykitPlanningStore((s) => s.executeMoveTask);
  const createMode = useEasykitPlanningStore((s) => s.createMode);
  const executeCreateTask = useEasykitPlanningStore((s) => s.executeCreateTask);
  const scheduleMode = useEasykitPlanningStore((s) => s.scheduleMode);
  const executeScheduleTask = useEasykitPlanningStore((s) => s.executeScheduleTask);

  // Get current day status for this driver/day
  const dayDriverStatuses = dayMarkers.get(dateKey);
  const currentStatus = dayDriverStatuses?.get(driverId) ?? null;

  const handleStatusChange = useCallback((newStatus: string) => {
    setDayStatus(dateKey, driverId, newStatus);
  }, [setDayStatus, dateKey, driverId]);

  const taskIds = tasks.map((t) => taskDndId(t.id));

  // Cross-cell DnD: cell acts as a drop target. Afhalingen (driverId 0) and "."
  // blocked-day cells are NOT disabled here — Afhalingen drop clears installateur,
  // and "." is purely visual per design.
  const { setNodeRef, isOver } = useDroppable({ id: dndCellId(driverId, dateKey) });

  return (
    <td
      ref={setNodeRef}
      className={`p-1 border-r border-gray-200 last:border-r-0 align-top min-h-[60px] ${getDayStatusBg(currentStatus)} ${isOver ? 'ring-2 ring-td-blue/40 ring-inset' : ''}`}
    >
      {/* Day status badge — only for actual drivers, not Afhalingen (driverId 0) */}
      {driverId !== 0 && (
        <div className="flex justify-end mb-1">
          <EasykitDayStatusBadge status={currentStatus} onStatusChange={handleStatusChange} />
        </div>
      )}

      {/* Task cards */}
      <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
        {tasks.map((task) => (
          <EasykitTaskCard key={task.id} task={task} isSelected={selectedTaskId === task.id} />
        ))}
      </SortableContext>

      {/* Overlay (read-only) — other tasks of this driver, not EK-owned. */}
      {overlayTasks.map((t) => (
        <EasykitOverlayCard key={`o-${t.id}`} task={t} />
      ))}

      {/* Empty state — three modes:
          - no EK + no overlay → dashed placeholder (cell fully bookable)
          - no EK + overlay   → small "Geen EK taak" caption (bookable but busy elsewhere)
          - has EK            → nothing extra */}
      {tasks.length === 0 && overlayTasks.length === 0 && !moveMode && (
        <div className="h-8 border border-dashed border-gray-200 rounded" />
      )}
      {tasks.length === 0 && overlayTasks.length > 0 && !moveMode && (
        <p className="text-[10px] italic text-slate-400 text-center mt-0.5">Geen EK taak</p>
      )}

      {/* Move mode: drop target */}
      {moveMode && (
        <button
          type="button"
          onClick={() => executeMoveTask(dateKey, driverId)}
          className="w-full mt-1 py-1.5 text-xs font-medium text-green-700 bg-green-50 border border-green-200 rounded hover:bg-green-100 transition-colors"
        >
          Taak neerzetten
        </button>
      )}

      {/* Schedule mode: place target */}
      {scheduleMode && (
        <button
          type="button"
          onClick={() => executeScheduleTask(dateKey, driverId)}
          className="w-full mt-1 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
        >
          Taak inplannen
        </button>
      )}

      {/* Create mode: place target */}
      {createMode && (
        <button
          type="button"
          onClick={() => executeCreateTask(dateKey, driverId)}
          className="w-full mt-1 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded hover:bg-purple-100 transition-colors"
        >
          {createMode.type === 'feestdag' ? 'Feestdag plaatsen' :
           createMode.type === 'verlof' ? 'Verlof plaatsen' :
           'Taak plaatsen'}
        </button>
      )}
    </td>
  );
}
